import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  LinearProgress,
  Alert,
  AlertTitle,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import { usersAPI, submissionsAPI } from '../../services/api';
import type { Program, UserManagement, BulkCreateResult } from '../../types';

interface BulkCreateDialogProps {
  open: boolean;
  onClose: () => void;
  program: Program;
  onSuccess?: () => void;
}

type DialogStep = 'configure' | 'processing' | 'results';

const BulkCreateDialog: React.FC<BulkCreateDialogProps> = ({
  open,
  onClose,
  program,
  onSuccess,
}) => {
  const [step, setStep] = useState<DialogStep>('configure');
  const [allUsers, setAllUsers] = useState<UserManagement[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [dueDate, setDueDate] = useState<Date | null>(
    program.endDate ? new Date(program.endDate) : null
  );
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<BulkCreateResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load users when dialog opens
  useEffect(() => {
    if (open) {
      loadUsers();
    }
  }, [open]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getAllForBulkCreate();
      setAllUsers(response);
      
      // Auto-select all users by default
      const allUserIds = new Set(response.map(u => u.userId));
      setSelectedUserIds(allUserIds);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return allUsers;
    
    const query = searchQuery.toLowerCase();
    return allUsers.filter(
      u =>
        u.displayName?.toLowerCase().includes(query) ||
        u.email?.toLowerCase().includes(query)
    );
  }, [allUsers, searchQuery]);

  // Users without managers (validation warning)
  const usersWithoutManagers = useMemo(() => {
    return allUsers.filter(u => {
      const managerIds = u.managerIds || [];
      return managerIds.length === 0;
    });
  }, [allUsers]);

  // Selected users without managers (will cause errors)
  const selectedUsersWithoutManagers = useMemo(() => {
    return Array.from(selectedUserIds)
      .map(id => allUsers.find(u => u.userId === id))
      .filter(u => u && (!u.managerIds || u.managerIds.length === 0));
  }, [selectedUserIds, allUsers]);

  const handleSelectAll = () => {
    if (selectedUserIds.size === filteredUsers.length) {
      // Deselect all filtered users
      const newSelection = new Set(selectedUserIds);
      filteredUsers.forEach(u => newSelection.delete(u.userId));
      setSelectedUserIds(newSelection);
    } else {
      // Select all filtered users
      const newSelection = new Set(selectedUserIds);
      filteredUsers.forEach(u => newSelection.add(u.userId));
      setSelectedUserIds(newSelection);
    }
  };

  const handleToggleUser = (userId: string) => {
    const newSelection = new Set(selectedUserIds);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      newSelection.add(userId);
    }
    setSelectedUserIds(newSelection);
  };

  const handleCreate = async () => {
    if (selectedUserIds.size === 0) {
      setError('Please select at least one user');
      return;
    }

    setStep('processing');
    setProgress(0);
    setError(null);

    try {
      // Simulate progress updates (batched emails take time)
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 5, 95));
      }, 500);

      const result = await submissionsAPI.bulkCreate({
        programId: program.programId,
        userIds: Array.from(selectedUserIds),
        dueDate: dueDate?.toISOString(),
      });

      clearInterval(progressInterval);
      setProgress(100);
      console.log('✅ Bulk creation result:', result);
      setResult(result);
      setStep('results');

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Bulk creation failed:', err);
      setError(err.response?.data?.error || 'Failed to create submissions');
      setStep('configure');
    }
  };

  const handleClose = () => {
    // Reset state
    setStep('configure');
    setSelectedUserIds(new Set());
    setSearchQuery('');
    setDueDate(program.endDate ? new Date(program.endDate) : null);
    setProgress(0);
    setResult(null);
    setError(null);
    onClose();
  };

  const renderConfigureStep = () => (
    <>
      <DialogContent dividers>
        {/* Program Info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Program Information
          </Typography>
          <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, border: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Program:</strong> {program.programName}
            </Typography>
            {program.allowance && (
              <Typography variant="body2" color="text.secondary">
                <strong>Allowance:</strong> ${program.allowance.toFixed(2)} per submission
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              <strong>Period:</strong> {new Date(program.startDate).toLocaleDateString()} -{' '}
              {new Date(program.endDate).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>

        {/* Due Date Picker */}
        {/* Due Date Picker */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Submission Deadline
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Employees will be notified to submit their expenses by this date
          </Typography>
          <TextField
            fullWidth
            type="date"
            label="Due Date"
            value={dueDate ? dueDate.toISOString().split('T')[0] : ''}
            onChange={(e) => {
              const newDate = e.target.value ? new Date(e.target.value) : null;
              setDueDate(newDate);
            }}
            InputLabelProps={{ shrink: true }}
            helperText="Default: Program end date"
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* User Selection */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Select Users
          </Typography>
          
          {/* Warnings */}
          {usersWithoutManagers.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Warning: Users Without Managers</AlertTitle>
              {usersWithoutManagers.length} user(s) have no manager assigned and cannot submit
              expenses. They will be excluded automatically.
            </Alert>
          )}

          {selectedUsersWithoutManagers.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Cannot Create Submissions</AlertTitle>
              {selectedUsersWithoutManagers.length} selected user(s) have no manager assigned.
              Please unselect them or assign managers first.
            </Alert>
          )}

          {/* Select All Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  filteredUsers.length > 0 &&
                  filteredUsers.every(u => selectedUserIds.has(u.userId))
                }
                indeterminate={
                  filteredUsers.some(u => selectedUserIds.has(u.userId)) &&
                  !filteredUsers.every(u => selectedUserIds.has(u.userId))
                }
                onChange={handleSelectAll}
              />
            }
            label={`Select All Users (${filteredUsers.length} users)`}
          />
        </Box>

        {/* Search Box */}
        <TextField
          fullWidth
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
          size="small"
        />

        {/* User List */}
        <Box
          sx={{
            maxHeight: 400,
            overflow: 'auto',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredUsers.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {searchQuery ? 'No users found matching your search' : 'No users available'}
              </Typography>
            </Box>
          ) : (
            <List dense>
              {filteredUsers.map((user, index) => {
                const hasManager = user.managerIds && user.managerIds.length > 0;
                const isSelected = selectedUserIds.has(user.userId);

                return (
                  <React.Fragment key={user.userId}>
                    {index > 0 && <Divider component="li" />}
                    <ListItem
                      button
                      onClick={() => handleToggleUser(user.userId)}
                      sx={{
                        bgcolor: !hasManager ? 'error.lighter' : undefined,
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          disabled={!hasManager}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {user.displayName || 'Unknown'}
                            {!hasManager && (
                              <Chip
                                label="No Manager"
                                size="small"
                                color="error"
                                icon={<WarningIcon />}
                              />
                            )}
                          </Box>
                        }
                        secondary={user.email}
                      />
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>

        {/* Selection Summary */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.lighter', borderRadius: 1 }}>
          <Typography variant="body2">
            <strong>Selected:</strong> {selectedUserIds.size} user(s)
          </Typography>
          {selectedUsersWithoutManagers.length > 0 && (
            <Typography variant="body2" color="error">
              <strong>⚠️ Cannot create:</strong> {selectedUsersWithoutManagers.length} user(s)
              without managers
            </Typography>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={
            selectedUserIds.size === 0 ||
            selectedUsersWithoutManagers.length > 0 ||
            loading
          }
        >
          Create {selectedUserIds.size} Submission{selectedUserIds.size !== 1 ? 's' : ''}
        </Button>
      </DialogActions>
    </>
  );

  const renderProcessingStep = () => (
    <>
      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6" gutterBottom>
            Creating Bulk Submissions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Progress: {selectedUserIds.size} submissions
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 8, borderRadius: 1, mb: 2 }}
          />
          <Typography variant="body2" color="text.secondary">
            {progress}% complete
          </Typography>
          <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
            <AlertTitle>Please wait</AlertTitle>
            Creating submissions and sending email notifications in batches of 50. This may take
            1-2 minutes.
          </Alert>
        </Box>
      </DialogContent>
    </>
  );

  const renderResultsStep = () => {
    if (!result) return null;

    const hasErrors = result.failedCount > 0 || result.summary.emailsFailed > 0;

    return (
      <>
        <DialogContent dividers>
          {/* Success Summary */}
          <Alert severity={hasErrors ? 'warning' : 'success'} sx={{ mb: 3 }}>
            <AlertTitle>
              {hasErrors ? 'Bulk Creation Completed with Warnings' : 'Bulk Creation Successful!'}
            </AlertTitle>
            Successfully created {result.successCount} submission{result.successCount !== 1 ? 's' : ''} for {result.summary.programName}
          </Alert>

          {/* Statistics */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Summary
            </Typography>
            <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, border: 1, borderColor: 'divider' }}>
              <Typography variant="body2">
                <strong>Total Requested:</strong> {result.totalRequested}
              </Typography>
              <Typography variant="body2" color="success.main">
                <strong>✓ Successfully Created:</strong> {result.successCount}
              </Typography>
              {result.failedCount > 0 && (
                <Typography variant="body2" color="error.main">
                  <strong>✗ Failed:</strong> {result.failedCount}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>📧 Emails Sent:</strong> {result.summary.emailsSent}
              </Typography>
              {result.summary.emailsFailed > 0 && (
                <Typography variant="body2" color="warning.main">
                  <strong>⚠️ Emails Failed:</strong> {result.summary.emailsFailed}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                <strong>Processing Time:</strong> {result.summary.processingTime}
              </Typography>
              {result.summary.dueDate && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Due Date:</strong>{' '}
                  {new Date(result.summary.dueDate).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Errors List */}
          {result.errors.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom color="error">
                Errors ({result.errors.length})
              </Typography>
              <List dense sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {result.errors.map((err, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <Divider />}
                    <ListItem>
                      <ListItemIcon>
                        <ErrorIcon color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary={err.displayName}
                        secondary={
                          <>
                            <Typography variant="caption" component="span" display="block">
                              {err.email}
                            </Typography>
                            <Typography variant="caption" component="span" color="error">
                              {err.error}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}

          {/* Email Failures */}
          {result.createdSubmissions.filter(s => !s.emailSent).length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom color="warning.main">
                Email Failures ({result.createdSubmissions.filter(s => !s.emailSent).length})
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                These submissions were created successfully, but email notifications failed to send.
                Users can still access them in the system.
              </Alert>
              <List dense sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {result.createdSubmissions
                  .filter(s => !s.emailSent)
                  .map((submission, index) => (
                    <React.Fragment key={submission.submissionId}>
                      {index > 0 && <Divider />}
                      <ListItem>
                        <ListItemIcon>
                          <EmailIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText
                          primary={submission.displayName}
                          secondary={
                            <>
                              <Typography variant="caption" component="span" display="block">
                                {submission.email}
                              </Typography>
                              {submission.emailError && (
                                <Typography variant="caption" component="span" color="warning.main">
                                  {submission.emailError}
                                </Typography>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
              </List>
            </Box>
          )}

          {/* Next Steps */}
          <Alert severity="info">
            <AlertTitle>Next Steps</AlertTitle>
            {result.failedCount > 0 && (
              <Typography variant="body2" paragraph>
                • Review errors above and create separate submissions for failed users if needed
              </Typography>
            )}
            <Typography variant="body2">
              • Users have been notified via email to add receipts and submit
            </Typography>
            <Typography variant="body2">
              • Monitor submissions in the Admin Dashboard
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={step === 'processing' ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={step === 'processing'}
    >
      <DialogTitle>
        {step === 'configure' && 'Create Bulk Submissions'}
        {step === 'processing' && 'Creating Submissions...'}
        {step === 'results' && 'Bulk Creation Results'}
      </DialogTitle>

      {step === 'configure' && renderConfigureStep()}
      {step === 'processing' && renderProcessingStep()}
      {step === 'results' && renderResultsStep()}
    </Dialog>
  );
};

export default BulkCreateDialog;